const NAV_MENU_BREAKPOINT = 1024;

class NavMenu extends HTMLElement {
  constructor() {
    super();
    this.activeLevel1Item = null;
    this.activeLevel2Item = null;
    this._unsubscribeDrawerOpen = null;
    this._unsubscribeDrawerClose = null;
    this._onResize = null;
    this._abortController = null;
    this._level1CloseTimer = null;
    this._level2CloseTimer = null;
  }

  connectedCallback() {
    this.desktopType = this.dataset.desktopType || 'dropdown';
    this.mobileType = this.dataset.mobileType || 'drawer';
    this.dropdownTrigger = this.dataset.dropdownTrigger || 'hover';
    this.parentLinkBehavior = this.dataset.parentLinkBehavior || 'link_and_toggle';

    this.hamburger = this.querySelector('.nav-menu__toggle');
    this.drawer = this.querySelector('theme-drawer');
    this.drawerPanel = this.drawer?.querySelector('[data-drawer-panel]');
    this.drawerClose = this.querySelector('.nav-menu__drawer-close');

    this.isDesktop = window.innerWidth >= NAV_MENU_BREAKPOINT;

    this._abortController = new AbortController();

    this.applyDrawerA11y();
    this.initDrawer();
    this.initSubmenus();
    this.initKeyboardNav();
    this.initResizeHandler();
  }

  applyDrawerA11y() {
    if (!this.drawerPanel) return;
    if (this.isDesktopMode()) {
      this.drawerPanel.removeAttribute('role');
      this.drawerPanel.removeAttribute('aria-modal');
    } else {
      this.drawerPanel.setAttribute('role', 'dialog');
      this.drawerPanel.setAttribute('aria-modal', 'true');
    }
  }

  disconnectedCallback() {
    this._unsubscribeDrawerOpen?.();
    this._unsubscribeDrawerClose?.();
    this._abortController?.abort();
    clearTimeout(this._level1CloseTimer);
    clearTimeout(this._level2CloseTimer);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  get utils() {
    return theme.utils;
  }

  isDesktopMode() {
    return this.isDesktop && (this.desktopType === 'dropdown' || this.desktopType === 'megamenu');
  }

  isInteractiveMegamenu() {
    return this.desktopType === 'megamenu' && !!this.querySelector('.nav-menu__container--megamenu-interactive');
  }

  initResizeHandler() {
    this._onResize = theme.utils.debounce(() => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth >= NAV_MENU_BREAKPOINT;

      if (wasDesktop !== this.isDesktop) {
        this.drawer?.close();
        this.closeAllSubmenus();
        this.applyDrawerA11y();
      } else if (this.isDesktop && this.isInteractiveMegamenu()) {
        const openPanel = this.querySelector('.nav-menu__submenu--level-2[aria-hidden="false"]');
        if (openPanel) {
          this._measureInteractivePanelWidth(openPanel);
          this._measureInteractivePanelHeight(openPanel);
        }
      }
    }, 100);

    window.addEventListener('resize', this._onResize);
  }

  initDrawer() {
    const { signal } = this._abortController;

    if (this.hamburger) {
      this.hamburger.addEventListener('click', () => this.drawer?.toggle(), { signal });
    }

    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => this.drawer?.close(), { signal });
    }

    this._unsubscribeDrawerOpen = theme.pubsub.subscribe(
      theme.pubsub.events.DRAWER_OPEN,
      ({ id }) => {
        if (id !== 'nav-menu-drawer') return;
        this.hamburger?.setAttribute('aria-expanded', 'true');
        this.closeAllSubmenus();
      }
    );

    this._unsubscribeDrawerClose = theme.pubsub.subscribe(
      theme.pubsub.events.DRAWER_CLOSE,
      ({ id }) => {
        if (id !== 'nav-menu-drawer') return;
        this.hamburger?.setAttribute('aria-expanded', 'false');
        this.closeAllSubmenus();
      }
    );
  }

  initSubmenus() {
    const { signal } = this._abortController;

    this.initLevel1Items();
    this.initLevel2Items();
    this.initBackButtons();
    this.initInteractivePanels();

    document.addEventListener('click', (e) => {
      if (this.isDesktopMode() && !this.contains(e.target)) {
        this.closeAllSubmenus();
      }
    }, { signal });
  }

  initInteractivePanels() {
    if (!this.isInteractiveMegamenu()) return;
    const { signal } = this._abortController;

    this.querySelectorAll('.nav-menu__submenu--level-2').forEach((panel) => {
      panel.addEventListener('mouseenter', () => {
        clearTimeout(this._level2CloseTimer);
        this._level2CloseTimer = null;
      }, { signal });

      panel.addEventListener('mouseleave', () => {
        if (!this.isDesktopMode()) return;
        this._level2CloseTimer = setTimeout(() => {
          this._level2CloseTimer = null;
          this._resetInteractivePanel(panel);
        }, 80);
      }, { signal });
    });
  }

  _resetInteractivePanel(panel) {
    panel.querySelectorAll('.nav-menu__item--level-2.is-active').forEach((item) => {
      const submenu = item.querySelector('.nav-menu__submenu--level-3');
      if (submenu) this.closeLevel2Submenu(item, submenu);
      else item.classList.remove('is-active');
    });
    if (this.activeLevel2Item?.closest('.nav-menu__submenu--level-2') === panel) {
      this.activeLevel2Item = null;
    }
    this.showContentBlocksForLevel1(panel);
  }

  initBackButtons() {
    const { signal } = this._abortController;

    this.querySelectorAll('.nav-menu__back').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const submenu = button.closest('.nav-menu__submenu');
        if (submenu) this.closeSubmenu(submenu);
      }, { signal });
    });
  }

  initLevel1Items() {
    const { signal } = this._abortController;

    this.querySelectorAll('.nav-menu__item--level-1').forEach((item) => {
      const submenu = item.querySelector('.nav-menu__submenu--level-2');
      if (!submenu) return;

      const toggle = item.querySelector('.nav-menu__submenu-toggle');
      const toggleLink = item.querySelector('.nav-menu__link--toggle');

      if (toggle) {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleLevel1Click(item, submenu);
        }, { signal });
      }

      if (toggleLink) {
        toggleLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleLevel1Click(item, submenu);
        }, { signal });
      }

      if (this.dropdownTrigger === 'hover') {
        item.addEventListener('mouseenter', () => {
          if (!this.isDesktopMode()) return;
          clearTimeout(this._level1CloseTimer);
          this._level1CloseTimer = null;
          this.closeAllLevel1Submenus(item);
          this.openLevel1Submenu(item, submenu);
        }, { signal });

        item.addEventListener('mouseleave', () => {
          if (!this.isDesktopMode()) return;
          this._level1CloseTimer = setTimeout(() => {
            this._level1CloseTimer = null;
            this.closeLevel1Submenu(item, submenu);
          }, 120);
        }, { signal });
      }
    });
  }

  handleLevel1Click(item, submenu) {
    if (this.isDesktopMode()) {
      const isOpen = submenu.getAttribute('aria-hidden') === 'false';
      if (isOpen) {
        this.closeLevel1Submenu(item, submenu);
      } else {
        this.closeAllLevel1Submenus(item);
        this.openLevel1Submenu(item, submenu);
      }
    } else {
      this.openLevel1Submenu(item, submenu);
    }
  }

  openLevel1Submenu(item, submenu) {
    const toggle = item.querySelector('.nav-menu__submenu-toggle');
    const toggleLink = item.querySelector('.nav-menu__link--toggle');

    this.utils.openPanel(toggle, submenu);
    if (toggleLink) toggleLink.setAttribute('aria-expanded', 'true');

    item.classList.add('is-active');
    this.activeLevel1Item = item;
    this.showContentBlocksForLevel1(submenu);

    if (this.isInteractiveMegamenu()) {
      this._measureInteractivePanelWidth(submenu);
      this._measureInteractivePanelHeight(submenu);
    }
  }

  closeLevel1Submenu(item, submenu) {
    const toggle = item.querySelector('.nav-menu__submenu-toggle');
    const toggleLink = item.querySelector('.nav-menu__link--toggle');

    this.utils.closePanel(toggle, submenu);
    if (toggleLink) toggleLink.setAttribute('aria-expanded', 'false');

    item.classList.remove('is-active');

    submenu.querySelectorAll('.nav-menu__submenu').forEach((nested) => {
      nested.setAttribute('aria-hidden', 'true');
    });

    submenu.querySelectorAll('[aria-expanded="true"]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
    });

    submenu.querySelectorAll('.is-active').forEach((i) => {
      i.classList.remove('is-active');
    });

    if (this.activeLevel1Item === item) this.activeLevel1Item = null;
    this.activeLevel2Item = null;
    this.hideAllContentBlocks();
  }

  closeAllLevel1Submenus(exceptItem = null) {
    this.querySelectorAll('.nav-menu__item--level-1').forEach((item) => {
      if (item === exceptItem) return;
      const submenu = item.querySelector('.nav-menu__submenu--level-2');
      if (submenu) this.closeLevel1Submenu(item, submenu);
    });
  }

  initLevel2Items() {
    const { signal } = this._abortController;

    this.querySelectorAll('.nav-menu__item--level-2').forEach((item) => {
      const submenu = item.querySelector('.nav-menu__submenu--level-3');
      const toggle = item.querySelector('.nav-menu__submenu-toggle');
      const toggleLink = item.querySelector('.nav-menu__link--toggle');
      const link = item.querySelector('.nav-menu__link--level-2');
      const itemUrl = item.dataset.itemUrl;

      if (toggle) {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleLevel2Click(item, submenu, itemUrl);
        }, { signal });
      }

      if (toggleLink) {
        toggleLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleLevel2Click(item, submenu, itemUrl);
        }, { signal });
      }

      if (link && !toggleLink) {
        link.addEventListener('click', () => {
          if (this.isDesktopMode()) this.setActiveLevel2Item(item, itemUrl);
        }, { signal });
      }

      if (this.dropdownTrigger === 'hover') {
        item.addEventListener('mouseenter', () => {
          if (!this.isDesktopMode()) return;
          clearTimeout(this._level2CloseTimer);
          this._level2CloseTimer = null;
          if (this.isInteractiveMegamenu()) {
            this.closeAllLevel2Submenus(item);
            if (itemUrl) this.setActiveLevel2Item(item, itemUrl);
            if (submenu) this.openLevel2Submenu(item, submenu);
          } else {
            if (itemUrl) this.showContentBlocksForUrl(itemUrl, item);
            if (submenu) this.openLevel2Submenu(item, submenu);
          }
        }, { signal });

        if (!this.isInteractiveMegamenu()) {
          item.addEventListener('mouseleave', () => {
            if (!this.isDesktopMode()) return;

            if (this.activeLevel2Item) {
              const activeUrl = this.activeLevel2Item.dataset.itemUrl;
              if (activeUrl) this.showContentBlocksForUrl(activeUrl, this.activeLevel2Item);
            } else {
              this.showParentContentBlocks(item);
            }

            if (submenu) this.closeLevel2Submenu(item, submenu);
          }, { signal });
        }
      }

      const focusTarget = link ?? toggleLink;
      if (focusTarget) {
        focusTarget.addEventListener('focus', () => {
          if (!this.isDesktopMode()) return;
          if (itemUrl) this.setActiveLevel2Item(item, itemUrl);
          if (submenu) this.openLevel2Submenu(item, submenu);
        }, { signal });
      }
    });
  }

  handleLevel2Click(item, submenu, itemUrl) {
    if (this.isDesktopMode()) {
      if (this.isInteractiveMegamenu()) {
        const isActive = item.classList.contains('is-active');
        const panel = item.closest('.nav-menu__submenu--level-2');
        if (isActive) {
          if (panel) this._resetInteractivePanel(panel);
          return;
        }
        this.closeAllLevel2Submenus(item);
        if (itemUrl) this.setActiveLevel2Item(item, itemUrl);
        if (submenu) this.openLevel2Submenu(item, submenu);
        return;
      }

      if (submenu) {
        const isOpen = submenu.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          this.closeLevel2Submenu(item, submenu);
          return;
        }
        this.closeAllLevel2Submenus(item);
        this.openLevel2Submenu(item, submenu);
      }

      if (itemUrl) this.setActiveLevel2Item(item, itemUrl);
    } else {
      if (submenu) this.openLevel2Submenu(item, submenu);
    }
  }

  openLevel2Submenu(item, submenu) {
    const toggle = item.querySelector('.nav-menu__submenu-toggle');
    const toggleLink = item.querySelector('.nav-menu__link--toggle');

    this.utils.openPanel(toggle, submenu);
    if (toggleLink) toggleLink.setAttribute('aria-expanded', 'true');
  }

  closeLevel2Submenu(item, submenu) {
    const toggle = item.querySelector('.nav-menu__submenu-toggle');
    const toggleLink = item.querySelector('.nav-menu__link--toggle');

    this.utils.closePanel(toggle, submenu);
    if (toggleLink) toggleLink.setAttribute('aria-expanded', 'false');

    item.classList.remove('is-active');

    if (this.activeLevel2Item === item) {
      this.activeLevel2Item = null;
      this.showParentContentBlocks(item);
    }
  }

  closeAllLevel2Submenus(exceptItem = null) {
    const parentSubmenu = exceptItem?.closest('.nav-menu__submenu--level-2');
    if (!parentSubmenu) return;

    parentSubmenu.querySelectorAll('.nav-menu__item--level-2').forEach((item) => {
      if (item === exceptItem) return;
      const submenu = item.querySelector('.nav-menu__submenu--level-3');
      if (submenu) this.closeLevel2Submenu(item, submenu);
      item.classList.remove('is-active');
    });
  }

  setActiveLevel2Item(item, itemUrl) {
    const parentSubmenu = item.closest('.nav-menu__submenu--level-2');
    if (!parentSubmenu) return;

    parentSubmenu.querySelectorAll('.nav-menu__item--level-2').forEach((i) => {
      if (i !== item) i.classList.remove('is-active');
    });

    item.classList.add('is-active');
    this.activeLevel2Item = item;
    this.showContentBlocksForUrl(itemUrl, item);
  }

  _blurFromSubmenu(submenu) {
    if (!submenu.contains(document.activeElement)) return;
    const panelId = submenu.dataset.panelId;
    const controller = panelId ? this.querySelector(`[aria-controls="${panelId}"]`) : null;
    const parentSubmenu = submenu.parentElement?.closest('.nav-menu__submenu');
    const candidates = [
      controller,
      parentSubmenu?.querySelector('.nav-menu__back'),
      this.drawerClose,
      this.hamburger,
    ];
    const target = candidates.find((el) => el && !submenu.contains(el));
    target?.focus();
  }

  closeSubmenu(submenu) {
    this._blurFromSubmenu(submenu);
    submenu.setAttribute('aria-hidden', 'true');

    submenu.querySelectorAll('.nav-menu__submenu').forEach((nested) => {
      this._blurFromSubmenu(nested);
      nested.setAttribute('aria-hidden', 'true');
    });

    submenu.querySelectorAll('[aria-expanded="true"]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
    });

    submenu.querySelectorAll('.is-active').forEach((i) => {
      i.classList.remove('is-active');
    });
  }

  closeAllSubmenus() {
    const anySubmenu = this.querySelector('.nav-menu__submenu');
    if (anySubmenu?.contains(document.activeElement)) {
      (this.drawerClose ?? this.hamburger)?.focus();
    }

    this.querySelectorAll('.nav-menu__submenu').forEach((submenu) => {
      submenu.setAttribute('aria-hidden', 'true');
    });

    this.querySelectorAll('[aria-expanded="true"]').forEach((el) => {
      if (el !== this.hamburger) el.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll('.is-active').forEach((item) => {
      item.classList.remove('is-active');
    });

    this.activeLevel1Item = null;
    this.activeLevel2Item = null;
    this.hideAllContentBlocks();
  }

  showContentBlocksForLevel1(submenu) {
    const parentUrl = submenu.dataset.parentUrl;
    if (!parentUrl) return;

    const container = submenu.querySelector('.nav-menu__content-blocks-container');
    if (!container) return;

    container.querySelectorAll('.header-content-block').forEach((block) => {
      block.classList.remove('is-active');
    });

    container.querySelectorAll(`.header-content-block[data-associated-url="${parentUrl}"]`).forEach((block) => {
      block.classList.add('is-active');
    });
  }

  showContentBlocksForUrl(url, menuItem) {
    const submenu = menuItem.closest('.nav-menu__submenu--level-2');
    if (!submenu) return;

    const container = submenu.querySelector('.nav-menu__content-blocks-container');
    if (!container) return;

    container.querySelectorAll('.header-content-block').forEach((block) => {
      block.classList.remove('is-active');
    });

    const matchingBlocks = container.querySelectorAll(`.header-content-block[data-associated-url="${url}"]`);
    matchingBlocks.forEach((block) => block.classList.add('is-active'));

    if (matchingBlocks.length === 0) {
      const parentUrl = submenu.dataset.parentUrl;
      if (parentUrl) {
        container.querySelectorAll(`.header-content-block[data-associated-url="${parentUrl}"]`).forEach((block) => {
          block.classList.add('is-active');
        });
      }
    }
  }

  showParentContentBlocks(menuItem) {
    const submenu = menuItem.closest('.nav-menu__submenu--level-2');
    if (!submenu) return;

    const container = submenu.querySelector('.nav-menu__content-blocks-container');
    if (!container) return;

    const parentUrl = submenu.dataset.parentUrl;
    if (!parentUrl) return;

    container.querySelectorAll('.header-content-block').forEach((block) => {
      block.classList.remove('is-active');
    });

    container.querySelectorAll(`.header-content-block[data-associated-url="${parentUrl}"]`).forEach((block) => {
      block.classList.add('is-active');
    });
  }

  hideAllContentBlocks() {
    this.querySelectorAll('.header-content-block').forEach((block) => {
      block.classList.remove('is-active');
    });
  }

  _measureInteractivePanelWidth(panel) {
    const l2List = panel.querySelector('.nav-menu__list--level-2');
    if (!l2List) return;

    const prev = l2List.style.width;
    l2List.style.width = 'max-content';
    const natural = l2List.scrollWidth;
    l2List.style.width = prev;

    const MAX_PX = 384;
    const clamped = Math.min(natural, MAX_PX);
    panel.style.setProperty('--megamenu-l2-width', `${clamped}px`);
  }

  _measureInteractivePanelHeight(panel) {
    const l2List = panel.querySelector('.nav-menu__list--level-2');
    const l2Height = l2List?.scrollHeight ?? 0;

    const panelTop = panel.getBoundingClientRect().top;
    const paddingBottom = parseFloat(getComputedStyle(panel).paddingBottom) || 0;

    const blocksToRestore = [];
    panel.querySelectorAll('.header-content-block').forEach((block) => {
      if (getComputedStyle(block).display === 'none') {
        block.classList.add('is-measuring');
        blocksToRestore.push(block);
      }
    });

    let maxDetailHeight = 0;
    panel.querySelectorAll('.nav-menu__item--level-2').forEach((item) => {
      const l3 = item.querySelector('.nav-menu__submenu--level-3');
      if (l3) {
        const l3Bottom = l3.getBoundingClientRect().bottom - panelTop + paddingBottom;
        maxDetailHeight = Math.max(maxDetailHeight, l3Bottom);
      }

      const url = item.dataset.itemUrl;
      const block = url
        ? panel.querySelector(`.header-content-block[data-associated-url="${url}"]`)
        : null;
      if (block) {
        const blockBottom = block.getBoundingClientRect().bottom - panelTop + paddingBottom;
        maxDetailHeight = Math.max(maxDetailHeight, blockBottom);
      }
    });

    blocksToRestore.forEach((block) => block.classList.remove('is-measuring'));

    const needed = Math.max(l2Height, maxDetailHeight);
    panel.style.minHeight = needed > 0 ? `${needed}px` : '';
  }

  _level1Items() {
    return [...this.querySelectorAll('.nav-menu__item--level-1')];
  }

  _focusableInSubmenu(submenu) {
    return [...submenu.querySelectorAll('a[href], button:not([disabled])')].filter(
      (el) => el.offsetParent !== null
    );
  }

  initKeyboardNav() {
    const { signal } = this._abortController;

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this._handleEscape();
        return;
      }

      if (!this.isDesktopMode()) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        this._handleLevel1Arrow(e);
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        this._handleLevel2Arrow(e);
      }
    }, { signal });
  }

  _handleEscape() {
    if (this.isDesktopMode()) {
      if (this.activeLevel1Item) {
        const toggle = this.activeLevel1Item.querySelector('.nav-menu__submenu-toggle, .nav-menu__link--toggle');
        this.closeAllSubmenus();
        toggle?.focus();
      }
    } else {
      const openSubmenu = this.querySelector('.nav-menu__submenu[aria-hidden="false"]');
      if (openSubmenu) {
        this.closeSubmenu(openSubmenu);
      }
    }
  }

  _handleLevel1Arrow(e) {
    const items = this._level1Items();
    const focused = document.activeElement?.closest('.nav-menu__item--level-1');
    const idx = items.indexOf(focused);
    if (idx === -1) return;

    e.preventDefault();
    const next = e.key === 'ArrowRight' ? items[idx + 1] : items[idx - 1];
    if (!next) return;

    const trigger = next.querySelector('.nav-menu__link--toggle, .nav-menu__link--level-1, .nav-menu__submenu-toggle');
    trigger?.focus();
  }

  _handleLevel2Arrow(e) {
    const openSubmenu = this.activeLevel1Item?.querySelector('.nav-menu__submenu--level-2[aria-hidden="false"]');
    if (!openSubmenu) return;

    const focusable = this._focusableInSubmenu(openSubmenu);
    const idx = focusable.indexOf(document.activeElement);
    if (idx === -1) return;

    e.preventDefault();
    const next = e.key === 'ArrowDown' ? focusable[idx + 1] : focusable[idx - 1];
    next?.focus();
  }
}

if (!customElements.get('nav-menu')) {
  customElements.define('nav-menu', NavMenu);
}
